import { Colors, Icon } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import useModalContext from 'components/context/modal';
import containScaleDown from 'editor-utils/polotno/objectFit/containScaleDown';
import coverScaleDown from 'editor-utils/polotno/objectFit/coverScaleDown';
import useEditorContext from '../../context/editorContext';
import useEditorItemContext from '../../context/editorItemContext';
import { usePolotnoStore } from '../storeContext';
import createPhoto from '../../lib/photos/createPhoto';

const StyledImg = styled.img`
  display: block;
  position: absolute;
`;

const Button = styled.button`
  display: flex;
  overflow: hidden;
  position: absolute;
  padding: ${({ padded }) => (padded ? '5px' : '0')};
  background: ${Colors.LIGHT_GRAY3};
  align-items: center;
  justify-content: center;
  transition: 0.1s linear left, 0.1s linear top, 0.2s linear background-color;

  &:hover {
    background: ${Colors.LIGHT_GRAY1};
  }

  ${({ $selected }) =>
    $selected &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${Colors.BLUE3};
        opacity: 0.6;
      }
    `}
`;

// text-overflow: ellipsis;
// white-space: nowrap;
const Label = styled.span`
  display: block;
  overflow: hidden;
  opacity: 0;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 5px;
  background: rgb(0, 0, 0);
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
  color: white;
  transform: translateY(30px);
  transition: 0.1s linear opacity, 0.1s ease-in transform;

  ${Button}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Action = styled.span`
  ${Icon} {
    color: white;
  }
`;

const Menu = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 5px 10px;
  min-height: 30px;
  opacity: 0;
  background: rgba(0, 0, 0, 0);
  background: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
  justify-content: flex-end;
  transform: translateY(-30px);
  transition: 0.1s linear opacity, 0.1s ease-in transform;

  ${Button}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  ${Action} {
    margin-left: 10px;
  }
`;

const FavoriteAction = styled(Action)`
  transition: 0.1s linear opacity, 0.1s ease-in transform;
  position: absolute;
  top: 5px;
  left: 10px;

  ${props =>
    props.$favorite
      ? css`
          opacity: 1;
          transform: translateY(0);
        `
      : css`
          opacity: 0;
          transform: translateY(-30px);
        `}

  ${Button}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FavoriteIcon = ({ isFavorite }) => (
  <svg
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
  >
    {isFavorite ? (
      <path d="M12 4.435c-1.989-5.399-12-4.597-12 3.568 0 4.068 3.06 9.481 12 14.997 8.94-5.516 12-10.929 12-14.997 0-8.118-10-8.999-12-3.568z" />
    ) : (
      <path d="M12 9.229c.234-1.12 1.547-6.229 5.382-6.229 2.22 0 4.618 1.551 4.618 5.003 0 3.907-3.627 8.47-10 12.629-6.373-4.159-10-8.722-10-12.629 0-3.484 2.369-5.005 4.577-5.005 3.923 0 5.145 5.126 5.423 6.231zm-12-1.226c0 4.068 3.06 9.481 12 14.997 8.94-5.516 12-10.929 12-14.997 0-7.962-9.648-9.028-12-3.737-2.338-5.262-12-4.27-12 3.737z" />
    )}
  </svg>
);

const PhotoGridItem = ({
  image: { blobUrl, element },
  item,
  onClick,
  size,
  style,
  showMenu,
  assetType,
  actions,
  selected,
}) => {
  const {
    id,
    item: { pages, favorite },
  } = useEditorItemContext();
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const polotnoStore = usePolotnoStore();

  const imgStyle = useMemo(() => {
    const parent = { width: style.width, height: style.height };
    const { x, y, width, height } =
      size === 'cover' ? coverScaleDown(parent, element) : containScaleDown(parent, element);
    return { height, left: x, top: y, width };
  }, [element, style]);

  const isFavorite = useMemo(() => {
    if (!favorite || !favorite[assetType]) {
      return false;
    }
    return !!favorite[assetType][item.id];
  }, [favorite, assetType, item]);

  const handleOnActionClick = (e, action) => {
    e.stopPropagation();

    switch (action) {
      case 'background':
        {
          const photo = createPhoto(item.url, { key: item.url });
          editorContext.addPhotos(id, [photo]);
          polotnoStore.activePage.set({
            background: photo.url,
          });
        }
        break;

      case 'delete':
        {
          const usedPages = pages
            .filter(page => page.placeholders.find(p => p.data?.photo_key === item.id))
            .filter(p => !!p);

          if (usedPages.length === 0) {
            modalContext.confirm('Do you really want to remove this photo?', {
              title: 'Removing photo',
              onConfirm: () => {
                editorContext.removePhoto(id, item.id);
              },
            });
          } else {
            modalContext.confirm(
              `This photo cannot be removed as it is currently in use on following pages: ${usedPages
                .map(page => page.pageLabel)
                .join(', ')}. Please remove it from these pages first.`,
              {
                title: 'Removing photo',
                closeable: false,
                confirmLabel: 'I understand',
              },
            );
          }
        }
        break;

      case 'favorite':
        editorContext.addFavorite(id, assetType, item);
        break;

      case 'unfavorite':
        editorContext.removeFavorite(id, assetType, item.id);
        break;

      default:
        break;
    }
  };

  return (
    <Button onClick={onClick} style={style} type="button" $selected={selected}>
      <StyledImg src={blobUrl} style={imgStyle} />
      {showMenu && (
        <>
          <Menu onClick={e => e.stopPropagation()}>
            {polotnoStore && actions.backgroundable && (
              <Action onClick={e => handleOnActionClick(e, 'background')} title="Set a background">
                <Icon icon="layout-grid" />
              </Action>
            )}
            {actions.deletable && (
              <Action onClick={e => handleOnActionClick(e, 'delete')} title="Delete">
                <Icon icon="trash" />
              </Action>
            )}
          </Menu>
          {actions.favoritable && (
            <FavoriteAction
              $favorite={isFavorite}
              onClick={e => handleOnActionClick(e, isFavorite ? 'unfavorite' : 'favorite')}
              title="Add to favorite"
            >
              <Icon icon={<FavoriteIcon isFavorite={isFavorite} />} />
            </FavoriteAction>
          )}
        </>
      )}
      {item.label && <Label>{item.label}</Label>}
    </Button>
  );
};

PhotoGridItem.propTypes = {
  image: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  style: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['cover', 'contain']),
  showMenu: PropTypes.bool,
  assetType: PropTypes.string,
  actions: PropTypes.object,
  selected: PropTypes.bool,
};

PhotoGridItem.defaultProps = {
  size: 'cover',
  showMenu: false,
  assetType: undefined,
  actions: {},
  selected: false,
};

export default PhotoGridItem;
